import React, { useState, useEffect } from 'react';
import { FaSearch } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { getWorkers } from '../features/workers/workerSlice';
import { assignForeman, removeForeman } from '../features/jobsites/jobsitesSlice';
import { toast } from 'react-toastify';

const SelectForemanModal = ({ isOpen, onClose, jobsiteId }) => {
  const [search, setSearch] = useState("");
  const [selectedWorkerId, setSelectedWorkerId] = useState(null);
  const [selectedForemanId, setSelectedForemanId] = useState(null);
  const dispatch = useDispatch();
  const { workers, isLoading, message } = useSelector((state) => state.workers);
  const { jobsites } = useSelector((state) => state.businessJobsites);

  useEffect(() => {
    if (isOpen) {
      dispatch(getWorkers());
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (message) {
      toast(message);
    }
  }, [message]);

  // Find the current jobsite and its foremen
  const currentJobsite = Array.isArray(jobsites)
    ? jobsites.find(js => js.id === jobsiteId)
    : null;
  const foremen = currentJobsite?.foremen || [];

  const filteredWorkers = Array.isArray(workers)
    ? workers.filter(
        (worker) =>
          worker.name.toLowerCase().includes(search.toLowerCase()) ||
          worker.lastName.toLowerCase().includes(search.toLowerCase())
      )
    : [];

  const handleAssignForeman = async () => {
    if (!selectedWorkerId || !jobsiteId){
        toast.error('Please select a worker and jobsite');
        return;
    } 
    dispatch(assignForeman({ workerId: selectedWorkerId, jobsiteId }));
    // Do not close modal, do not clear selection
  };

  const handleRemoveForeman = async () => {
    if (!selectedForemanId || !jobsiteId) {
      toast.error('Please select a foreman to remove');
      return;
    }
    dispatch(removeForeman({ workerId: selectedForemanId, jobsiteId }));
    // Do not close modal, do not clear selection
  };

  // Reset selections when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedWorkerId(null);
      setSelectedForemanId(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-3xl w-full">
        <h2 className="text-lg font-bold mb-2 text-blue-700">Select Foreman</h2>
        <p className="mb-4 text-sm text-gray-700">
          Foreman will be able to post notes to for all workers to see and invite new workers to join.
        </p>
        <div className="flex gap-6">
          {/* Left column: All workers */}
          <div className="flex-1">
            <div className="relative mb-4">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                <FaSearch />
              </span>
              <input
                type="text"
                className="w-full border border-gray-300 rounded pl-10 pr-3 py-2"
                placeholder="Search for a worker..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <div className="overflow-y-auto max-h-64 mb-4">
              {isLoading ? (
                <div className="text-center text-gray-500 py-4">Loading...</div>
              ) : filteredWorkers.length === 0 ? (
                <div className="text-center text-gray-500 py-4">No workers found.</div>
              ) : (
                filteredWorkers.map((worker) => (
                  <div
                    key={worker.id}
                    className={`flex items-center mb-2 rounded-full px-4 py-2 cursor-pointer transition-colors border ${selectedWorkerId === worker.id ? 'bg-orangem text-white border-orangem' : 'bg-sage text-charcoal border-sage'} hover:bg-orangem/80 hover:text-white`}
                    onClick={() => {
                      setSelectedWorkerId(worker.id);
                      setSelectedForemanId(null);
                    }}
                  >
                    <img
                      src={worker.profilePicture || '/workerSilhoutte.png'}
                      alt={worker.name}
                      className="w-16 h-16 rounded-full object-cover mr-3 border"
                    />
                    <span className="font-semibold text-base">{worker.name} {worker.lastName}</span>
                  </div>
                ))
              )}
            </div>
          </div>
          {/* Right column: Current foremen */}
          <div className="flex-1 border-l pl-6">
            <h3 className="font-semibold mb-2 text-blue-700">Current Foremen</h3>
            <div className="overflow-y-auto max-h-64 mb-4">
              {foremen.length === 0 ? (
                <div className="text-center text-gray-500 py-4">No foremen assigned.</div>
              ) : (
                foremen.map((foreman) => (
                  <div
                    key={foreman.id}
                    className={`flex items-center mb-2 rounded-full px-4 py-2 cursor-pointer transition-colors border ${selectedForemanId === foreman.id ? 'bg-red-500 text-white border-red-500' : 'bg-sage text-charcoal border-sage'} hover:bg-red-400/80 hover:text-white`}
                    onClick={() => {
                      setSelectedForemanId(foreman.id);
                      setSelectedWorkerId(null);
                    }}
                  >
                    <img
                      src={foreman.profilePicture || '/workerSilhoutte.png'}
                      alt={foreman.name}
                      className="w-16 h-16 rounded-full object-cover mr-3 border"
                    />
                    <span className="font-semibold text-base">{foreman.name} {foreman.lastName}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-between mt-4">
          <button
            className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300"
            onClick={onClose}
          >
            Close
          </button>
          {selectedWorkerId && (
            <button
              className="px-4 py-2 rounded-full bg-orangem text-white font-semibold shadow hover:bg-orange-500 transition"
              onClick={handleAssignForeman}
            >
              Select as Foreman
            </button>
          )}
          {selectedForemanId && (
            <button
              className="px-4 py-2 rounded-full bg-red-500 text-white font-semibold shadow hover:bg-red-600 transition"
              onClick={handleRemoveForeman}
            >
              Remove as Foreman
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SelectForemanModal;
